import { useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { STUDYGROUP_MEMBERSHIP_STATUS_MEMBER } from "@/constants";
import { studyGroupMembershipApi } from "@/lib/humhub-api/studyGroupMembership";
import { completeMembershipTransition } from "@/lib/study-group/completeMembershipTransition";
import type { EventApiResponse } from "@/lib/humhub-api/types";

export type ForceJoinResult = {
  success: boolean;
  action: "force_joined" | "already_member" | "failed";
  groupName: string;
  error?: string;
};

interface UseForceJoinStudyGroupOptions {
  onComplete?: (result: ForceJoinResult) => void;
}

interface UseForceJoinStudyGroupReturn {
  handleForceJoin: (eventData: EventApiResponse) => Promise<ForceJoinResult>;
}

/**
 * スタディグループへの強制参加専用フック
 * 認可なしイベント参加時に使用
 */
export const useForceJoinStudyGroup = (
  options: UseForceJoinStudyGroupOptions = {},
): UseForceJoinStudyGroupReturn => {
  const { onComplete } = options;
  const queryClient = useQueryClient();

  const handleForceJoin = useCallback(
    async (eventData: EventApiResponse): Promise<ForceJoinResult> => {
      if (!eventData.container) {
        const result: ForceJoinResult = {
          success: false,
          action: "failed",
          groupName: "",
          error: "No container information",
        };
        onComplete?.(result);
        return result;
      }

      const { id: studyGroupId, name: groupName } = eventData.container;

      try {
        // Step 1: 現在の参加状態を確認
        let membershipStatus = null;
        try {
          membershipStatus =
            await studyGroupMembershipApi.getStudyGroupYourMembership(
              studyGroupId,
            );
        } catch (error) {
          console.warn("Failed to get membership status:", error);
        }

        // Step 2: 既に参加済みの場合
        if (
          membershipStatus &&
          membershipStatus.status === STUDYGROUP_MEMBERSHIP_STATUS_MEMBER
        ) {
          const result: ForceJoinResult = {
            success: true,
            action: "already_member",
            groupName,
          };
          onComplete?.(result);
          return result;
        }

        // Step 3: 強制参加実行
        const transitionResult = await completeMembershipTransition({
          queryClient,
          studyGroupId,
          action: "force_join",
        });
        if (!transitionResult.lastVisitUpdated) {
          console.warn("Last visit update failed after force join", {
            studyGroupId,
          });
        }

        const result: ForceJoinResult = {
          success: true,
          action: "force_joined",
          groupName,
        };
        onComplete?.(result);
        return result;
      } catch (error) {
        console.error("Study group force join failed:", error);

        const result: ForceJoinResult = {
          success: false,
          action: "failed",
          groupName,
          error: error instanceof Error ? error.message : "Unknown error",
        };
        onComplete?.(result);
        return result;
      }
    },
    [onComplete, queryClient],
  );

  return { handleForceJoin };
};
